/**
 * Shrinks a tooltip panel to the text inside it.
 *
 * A panel is as wide as its cap, because `max-content` for a sentence is the
 * whole thing on one line and always overshoots. The lines inside it are
 * shorter than that, and the leftover reads as an unfilled box.
 *
 * Measuring the widest line and setting that as the width is safe: every line
 * already fitted within it, so wrapping is unchanged and only the slack goes.
 * One measurement, no search.
 */
(() => {
  const SELECTOR = '.trust-list__tip-content, .inline-tip__content';

  const fit = (panel) => {
    panel.style.width = '';
    const cs = getComputedStyle(panel);
    // Hidden by visibility, not display, so it still has a layout to measure
    if (cs.display === 'none') return;

    const full = panel.getBoundingClientRect().width;
    const tall = panel.scrollHeight;
    if (!full || !tall) return;

    const frame =
      parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) +
      parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);

    const range = document.createRange();
    range.selectNodeContents(panel);
    let widest = 0;
    for (const rect of range.getClientRects()) {
      if (rect.width > widest) widest = rect.width;
    }
    if (!widest) return;

    // The widest line is the floor, the current width the ceiling. Narrow by
    // halves and keep only what does not add a line - `text-wrap: pretty` is
    // free to break differently at a different width, so the widest line on
    // its own is not a safe answer.
    let lo = Math.ceil(widest + frame);
    let hi = Math.ceil(full);
    let best = hi;
    for (let i = 0; i < 8 && lo < hi; i += 1) {
      const mid = Math.floor((lo + hi) / 2);
      panel.style.width = `${mid}px`;
      if (panel.scrollHeight <= tall) {
        best = mid;
        hi = mid - 1;
      } else {
        lo = mid + 1;
      }
    }
    panel.style.width = `${best}px`;
  };

  const fitAll = () => document.querySelectorAll(SELECTOR).forEach(fit);

  // Measuring at load can be wrong: the webfont may not have been requested yet
  // for a panel that is hidden, and a fallback face fits more on a line. So the
  // panel is measured again the moment it is about to be shown, which is the
  // only point where the rendering is certainly final.
  const onReveal = (event) => {
    const wrap = event.target.closest('.trust-list__tip, .inline-tip');
    if (!wrap) return;
    const panel = wrap.querySelector(SELECTOR);
    if (panel) fit(panel);
  };

  const run = () => {
    fitAll();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitAll);
    document.addEventListener('pointerenter', onReveal, true);
    document.addEventListener('focusin', onReveal, true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  let timer;
  window.addEventListener('resize', () => {
    clearTimeout(timer);
    timer = setTimeout(fitAll, 150);
  });

  // The cart and the product form re-render sections in place
  document.addEventListener('shopify:section:load', fitAll);
})();
